import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { API_BASE_URL } from '@/config/api';
import AdminSidebar from '@/components/admin/AdminSidebar';

const AdminSiteAccess = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin) {
      fetchSettings();
    }
  }, [admin]);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/site-gate`, {
        withCredentials: true,
      });
      setEnabled(response.data.enabled);
      setUpdatedAt(response.data.updated_at);
      setUpdatedBy(response.data.updated_by);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load site access settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    const nextEnabled = !enabled;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/site-gate`,
        { enabled: nextEnabled },
        { withCredentials: true }
      );
      setEnabled(response.data.enabled);
      setUpdatedAt(response.data.updated_at);
      setUpdatedBy(response.data.updated_by);
      setSuccess(
        nextEnabled
          ? 'Password page activated. Visitors must sign in to view the website.'
          : 'Password page deactivated. The website is now publicly accessible.'
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update site access settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-site-access-page">
      <div className="lg:hidden bg-white border-b border-[#E0DED8] px-4 py-3 flex items-center justify-between">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-5" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        <AdminSidebar
          admin={admin}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        <main className="flex-1 p-6 lg:p-10 min-h-screen">
          <div className="mb-8">
            <h1 className="text-2xl font-heading text-[#1A1A1A]">Site Access</h1>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Control the private preview password page for unpublished access.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-sm flex items-start gap-3">
              <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}

          <div className="bg-white rounded-sm border border-[#E0DED8] p-6 md:p-8 max-w-2xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#F5F3EF] rounded-full flex items-center justify-center flex-shrink-0">
                <Lock size={20} className="text-[#8B7355]" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-lg text-[#1A1A1A] mb-2">Password Page</h2>
                <p className="text-sm text-[#6B6B6B] mb-6">
                  When active, visitors must enter the preview credentials before accessing the public website.
                  Admin pages remain accessible without the preview password.
                </p>

                <div className="flex items-center justify-between gap-4 p-4 bg-[#F5F3EF] rounded-sm">
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      {enabled ? 'Password page is active' : 'Password page is inactive'}
                    </p>
                    <p className="text-xs text-[#9B9B9B] mt-1">
                      {enabled
                        ? 'The website is hidden behind the preview login.'
                        : 'The website is publicly accessible.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggle}
                    disabled={saving}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                      enabled ? 'bg-[#1A1A1A]' : 'bg-[#D8D5CE]'
                    } disabled:opacity-50`}
                    data-testid="site-access-toggle"
                    aria-pressed={enabled}
                    aria-label={enabled ? 'Deactivate password page' : 'Activate password page'}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {updatedAt && (
                  <p className="text-xs text-[#9B9B9B] mt-4">
                    Last updated {new Date(updatedAt).toLocaleString()}
                    {updatedBy ? ` by ${updatedBy}` : ''}
                  </p>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminSiteAccess;
